import { Component } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * RouteErrorBoundary — a per-route error boundary used INSIDE the
 * authenticated dashboard. Keeps the sidebar + topbar interactive so the
 * user is never trapped on a broken module. The boundary auto-resets
 * whenever React Router changes the pathname — this is done by remounting
 * (keyed on `pathname`) rather than mutating state from within render.
 */
class _InnerRouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[Zynthoro] Module render error:", error, info?.componentStack);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    this.props.onGoHome?.();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        className="rounded-xl border border-[#eef1f7] bg-white p-8"
        data-testid="module-error-boundary"
      >
        <p
          className="text-[11px] font-semibold uppercase tracking-wider text-[#1A4FFF] mb-2"
        >
          Zynthoro
        </p>
        <h2 className="text-[22px] font-bold text-[#0A1628] mb-3">
          This page ran into a problem.
        </h2>
        <p className="text-[14px] text-[#4a5568] leading-relaxed mb-6 max-w-xl">
          This page could not be displayed. Saving cannot be confirmed from this screen. Check recent changes before repeating an action. You can retry this page or return to your dashboard.
        </p>
        <div className="flex gap-3 flex-wrap">
          <button
            type="button"
            onClick={this.handleRetry}
            data-testid="module-error-retry"
            className="zy-btn-primary text-[13px]"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={this.handleGoHome}
            data-testid="module-error-dashboard"
            className="text-[13px] px-4 py-2 border border-[#eef1f7] rounded-md font-semibold text-[#0A1628] hover:bg-[#F7F8FA]"
          >
            Back to dashboard
          </button>
        </div>
        {process.env.NODE_ENV !== "production" && this.state.error && (
          <pre
            className="mt-6 text-[11px] text-[#c00] whitespace-pre-wrap break-words max-w-full overflow-auto"
            data-testid="module-error-detail"
          >
            {String(this.state.error?.message || this.state.error)}
          </pre>
        )}
      </div>
    );
  }
}

export default function RouteErrorBoundary({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const workspaceKey = user?.active_workspace || user?.id || "";
  // Remounting the inner boundary on pathname change clears its error
  // state automatically — no stale "something went wrong" screen when
  // the user navigates away from a broken page.
  return (
    <_InnerRouteErrorBoundary
      key={`${user?.id || ""}:${workspaceKey}:${location.pathname}`}
      onGoHome={() => navigate("/dashboard")}
    >
      {children}
    </_InnerRouteErrorBoundary>
  );
}
